import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';

import {
	addMonths,
	eachDayOfInterval,
	endOfDay,
	endOfMonth,
	endOfWeek,
	getISOWeek,
	isSameDay,
	isWithinInterval,
	startOfDay,
	startOfMonth,
	startOfWeek,
} from '@/app/utils/date';

type WeekType = { week: number; dates: DateTime[] }[];
type DispatchType = {
	prev: () => void;
	next: () => void;
	date: DateTime;
	isDateSelected: (value: DateTime) => boolean;
	isWeekSelected: (weekNumber: number) => boolean;
	isLastSelectedDate: (value: DateTime) => boolean;
	isFirstSelected: (value: DateTime) => boolean;
};

export const useCalendarDates = (
	value?: { start: DateTime; end: DateTime },
	focusDate?: DateTime
): [WeekType, DispatchType] => {
	const currentValue = useMemo(() => {
		return (
			value ?? {
				start: startOfDay(DateTime.now()),
				end: endOfDay(DateTime.now()),
			}
		);
	}, [value]);

	const [date, setDate] = useState((focusDate ?? currentValue?.start) || DateTime.now());

	const dates = useMemo(() => {
		const monthStarts = startOfMonth(date);
		const monthEnds = endOfMonth(date);

		const start = startOfWeek(monthStarts);
		const end = endOfWeek(monthEnds);

		return eachDayOfInterval({ start, end });
	}, [date]);

	const prev = useCallback(() => {
		setDate(addMonths(date, -1));
	}, [date]);

	const next = useCallback(() => {
		setDate(addMonths(date, 1));
	}, [date]);

	const isDateSelected = useCallback(
		(date: DateTime) => {
			return isWithinInterval(date, currentValue);
		},
		[currentValue]
	);

	const isLastSelectedDate = useCallback(
		(date: DateTime) => {
			return isDateSelected(date) && isSameDay(date, currentValue.end);
		},
		[isDateSelected, currentValue.end]
	);
	const isFirstSelected = useCallback(
		(date: DateTime) => {
			return isDateSelected(date) && isSameDay(date, currentValue.start);
		},
		[isDateSelected, currentValue.start]
	);

	const weeksValue = useMemo(() => {
		const weeks: WeekType = [];
		const numberOfWeeks = dates.length / 7;

		let weekStart = 0;
		let weekEnd = 7;

		for (let i = 0; i < numberOfWeeks; i++) {
			const firstDate = dates[weekStart];
			if (firstDate !== null) {
				weeks.push({
					week: getISOWeek(firstDate),
					dates: [],
				});
			}

			for (let index = weekStart; index < weekEnd; index++) {
				const currentDate = dates[index];
				if (currentDate !== null) {
					weeks[i].dates.push(currentDate);
				}
			}

			weekStart += 7;
			weekEnd += 7;
		}

		return weeks;
	}, [dates]);

	const isWeekSelected = useCallback(
		(weekNumber: number) => {
			return (
				weeksValue
					.find((w) => w.week === weekNumber)
					?.dates.every((d) => isWithinInterval(d, currentValue)) ?? false
			);
		},
		[currentValue, weeksValue]
	);

	return [
		weeksValue,
		{ prev, next, date, isDateSelected, isWeekSelected, isLastSelectedDate, isFirstSelected },
	];
};
